import { useCallback, useMemo } from 'react'
import { useQuery, useMutation } from '@maui/react-query'
import { combineSets } from '@/utils/combineSets'
import { useMatiApiClient } from './client'

export const followingTypes = [
  {
    name: 'actor',
    requestType: 'actors',
    key: ['followingActors'],
    idPrefix: 'threat-actor--',
  },
  {
    name: 'malware',
    requestType: 'malware',
    key: ['followingMalware'],
    idPrefix: 'malware--',
  },
  {
    name: 'campaign',
    requestType: 'campaigns',
    key: ['followingCampaigns'],
    idPrefix: 'campaign--',
  },
  {
    name: 'vulnerability',
    requestType: 'vulns',
    key: ['followingVulns'],
    idPrefix: 'vulnerability--',
  }
]

export const followingsMap = followingTypes.reduce((map, type) => {
  map[type.name] = type
  return map
}, {})

export const useFollowingsQuery = (type, options = {}) => {
  if (!type || !followingsMap[type]) {
    throw new Error(
      'A valid type is required to fetch followings'
    )
  }
  const api = useMatiApiClient('following')
  return useQuery({
    queryFn: () => {
      const followingType = followingsMap[type].requestType
      return api.fetchFollowings(followingType)
    },
    queryKey: followingsMap[type].key,
    ...options
  })
}

export const useFollowingsMutation = () => {
  const api = useMatiApiClient('following')

  return useMutation({
    optimisticUpdateKey: ({ type }) => followingsMap[type].key,
    optimisticUpdateFn: (_, vars) => {
      return vars.ids
    },
    mutationFn: ({ ids, type }) => {
      const requestType = followingsMap[type].requestType
      const response = api.updateFollowings({ ids, type: requestType })
      return response
    },
  })
}

export const useMaApiFollowing = () => {
  const { mutate } = useFollowingsMutation()

  // NOTE I considered using useQueries here but struggled with a clean solution for hook
  // dependencies given the queries return value isn't memoized, only the data is. As the queries
  // are technically known here, it felt unnecessary to try to figure out a less literal solution.
  const actors = useFollowingsQuery('actor')
  const malware = useFollowingsQuery('malware')
  const campaigns = useFollowingsQuery('campaign')
  const vulns = useFollowingsQuery('vulnerability')

  const isLoading = [actors, malware, campaigns, vulns].some(({ isLoading }) => isLoading)

  const following = useMemo(() => {
    const map = {
      actor: new Set(actors.data || []),
      malware: new Set(malware.data || []),
      campaign: new Set(campaigns.data || []),
      vulnerability: new Set(vulns.data || []),
    }

    map.all = combineSets(...Object.values(map))

    return map
  }, [
    actors.data,
    malware.data,
    campaigns.data,
    vulns.data,
  ])

  const testFollowing = useCallback(id => {
    return !!id && !!following.all.has(id)
  }, [following])

  const updateFollowing = useCallback((id, follow) => {
    const type = followingTypes.find(type => id && id.startsWith(type.idPrefix))

    if (!type) {
      console.warn(`updateFollowing got unexpected id: ${id}`)
      return
    }

    const set = new Set(following[type.name])
    const followed = set.has(id)

    if (followed === follow) {
      return
    }

    follow ? set.add(id) : set.delete(id)

    mutate({ type: type.name, ids: [...set] })
  }, [
    mutate,
    following,
  ])

  const toggleFollowing = useCallback(id => {
    updateFollowing(id, !testFollowing(id))
  }, [updateFollowing, testFollowing])

  return {
    following,
    isLoading,
    testFollowing,
    toggleFollowing,
    updateFollowing,
  }
}
